import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useAuth } from '@clerk/clerk-react';
import { Bot, Loader2, MessageSquare, Send, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://erg-shoot.des-466.workers.dev/api';

interface ChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkoutGenerated?: (workout: WorkoutData) => void;
}

interface WorkoutData {
  name: string;
  description?: string;
  movements?: string[];
  scoring_type: string;
  scoring_direction?: string;
  equipment?: string[];
  scheduled_date?: string; // Optional - user will set manually
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

// Quick reply button options based on what the AI is asking about
interface QuickReplyOption {
  label: string;
  value: string;
}

// Detect what question the AI is asking and return relevant options
// workoutGenerated: if true, skip question-based replies and show approval options
function getQuickReplies(
  lastMessage: string | undefined,
  workoutGenerated: boolean
): QuickReplyOption[] {
  if (!lastMessage) return [];

  const lower = lastMessage.toLowerCase();

  // If workout is already generated, don't show more question options
  // The "Use This" button handles approval - just show modify option if needed
  if (workoutGenerated) {
    // Only show options if AI is asking for confirmation
    if (
      lower.includes('ready to') ||
      lower.includes('give it a try') ||
      lower.includes('sound good') ||
      lower.includes('what do you think')
    ) {
      return [
        { label: '✅ Looks great!', value: 'Yes, this workout looks perfect!' },
        { label: '✏️ Make changes', value: "I'd like to modify something about this workout" },
      ];
    }
    // Workout exists but no question - don't show quick replies (Use This button is visible)
    return [];
  }

  // Target type question (moving vs stationary) - check FIRST, more specific
  if (lower.includes('moving target') || lower.includes('stationary')) {
    return [
      { label: '🎯 Stationary', value: 'Stationary target (fixed position)' },
      { label: '🏃 Moving', value: 'Moving target (adds difficulty)' },
    ];
  }

  // Yes/No confirmation questions
  if (
    lower.includes('is that correct?') ||
    lower.includes('sound good?') ||
    lower.includes('ready to use')
  ) {
    return [
      { label: '✅ Yes', value: 'Yes, that sounds great!' },
      { label: '✏️ Change something', value: "I'd like to modify something" },
    ];
  }

  // Number of shots question - be specific to avoid false positives
  // Only show if asking "how many shots" directly, not when discussing shots in context
  if (
    (lower.includes('how many') && lower.includes('shot') && !lower.includes('how many rounds')) ||
    lower.includes('number of shots') ||
    lower.includes('shots per round?') ||
    lower.includes('shots do you want')
  ) {
    return [
      { label: '5 shots', value: '5 target shots per round' },
      { label: '10 shots', value: '10 target shots per round' },
      { label: '15 shots', value: '15 target shots per round' },
    ];
  }

  // Rounds question - check BEFORE generic patterns
  if (
    lower.includes('how many rounds') ||
    lower.includes('number of rounds') ||
    lower.includes('rounds would you like') ||
    lower.includes('rounds do you want')
  ) {
    return [
      { label: '3 rounds', value: '3 rounds' },
      { label: '5 rounds', value: '5 rounds' },
      { label: '7 rounds', value: '7 rounds' },
    ];
  }

  // Rest/breaks question
  if (
    lower.includes('rest time') ||
    lower.includes('rest between') ||
    lower.includes('breaks between') ||
    lower.includes('want any rest') ||
    lower.includes('want any breaks')
  ) {
    return [
      { label: 'No rest', value: 'No rest between rounds' },
      { label: '30 sec rest', value: '30 seconds rest between rounds' },
      { label: '1 min rest', value: '1 minute rest between rounds' },
    ];
  }

  // Include shooting question - be more specific
  if (
    lower.includes('include shooting') ||
    lower.includes('with target shooting') ||
    lower.includes('add shooting') ||
    (lower.includes('shooting') && lower.includes('cardio'))
  ) {
    return [
      { label: '🎯 Yes, with shooting', value: 'Yes, include ErgShoot target shooting' },
      { label: '🚣 No, cardio only', value: 'No shooting, just cardio on the erg' },
    ];
  }

  // Equipment question - which erg to use
  // Check for combinations of equipment keywords in the same message
  const hasRower = lower.includes('rower') || lower.includes('row');
  const hasBike = lower.includes('bike') || lower.includes('bikeerg');
  const hasSki = lower.includes('ski') || lower.includes('skierg');
  const equipmentMentions = [hasRower, hasBike, hasSki].filter(Boolean).length;

  if (
    lower.includes('which erg') ||
    lower.includes('what equipment') ||
    lower.includes('kind of equipment') ||
    lower.includes('go with the') ||
    equipmentMentions >= 2 // At least 2 equipment types mentioned = asking which one
  ) {
    return [
      { label: '🚣 Rower', value: 'Concept2 Rower' },
      { label: '🚴 BikeErg', value: 'Concept2 BikeErg' },
      { label: '⛷️ SkiErg', value: 'Concept2 SkiErg' },
    ];
  }

  // Scoring/format question
  if (
    lower.includes('scored') ||
    lower.includes('scoring') ||
    lower.includes('for time') ||
    lower.includes('amrap') ||
    lower.includes('format')
  ) {
    return [
      { label: '⏱️ For Time', value: 'For Time (fastest wins)' },
      { label: '🔄 AMRAP', value: 'AMRAP (as many rounds as possible)' },
      { label: '💪 For Reps', value: 'For Reps (most reps wins)' },
    ];
  }

  // Distance question
  if (
    lower.includes('distance') ||
    lower.includes('how far') ||
    lower.includes('how many meters')
  ) {
    return [
      { label: '250m', value: '250 meters' },
      { label: '500m', value: '500 meters' },
      { label: '1000m', value: '1000 meters' },
      { label: '2000m', value: '2000 meters' },
    ];
  }

  // Duration question
  if (
    lower.includes('duration') ||
    lower.includes('how long') ||
    lower.includes('how many minutes')
  ) {
    return [
      { label: '5 min', value: '5 minutes' },
      { label: '10 min', value: '10 minutes' },
      { label: '15 min', value: '15 minutes' },
      { label: '20 min', value: '20 minutes' },
    ];
  }

  // Track/difficulty question
  if (lower.includes('track') || lower.includes('rx') || lower.includes('scaled')) {
    return [
      { label: 'RX', value: 'RX track (full intensity)' },
      { label: 'Scaled', value: 'Scaled track (modified)' },
      { label: 'Foundations', value: 'Foundations track (beginner)' },
    ];
  }

  return [];
}

function parseWorkoutFromMessage(content: string): WorkoutData | null {
  // Try to extract JSON from markdown code blocks
  const jsonMatch = content.match(/```json\s*([\s\S]*?)```/);
  if (jsonMatch?.[1]) {
    try {
      const parsed = JSON.parse(jsonMatch[1]);
      // Validate required fields (date is now optional)
      if (parsed.name && parsed.scoring_type) {
        // Remove date so user must set it manually
        const { scheduled_date, ...workoutWithoutDate } = parsed;
        return workoutWithoutDate as WorkoutData;
      }
    } catch {
      // JSON parse failed
    }
  }
  return null;
}

// Format message to hide JSON and show friendly summary
function formatMessageForDisplay(content: string, workout: WorkoutData | null): string {
  if (!workout) return content;

  // Remove JSON block from display
  const withoutJson = content.replace(/```json[\s\S]*?```/g, '').trim();

  // Build a friendly workout summary
  const summary = [
    `**${workout.name}**`,
    workout.description ? `\n${workout.description}` : '',
    workout.movements?.length
      ? `\n\n**Movements:**\n${workout.movements.map((m) => `• ${m}`).join('\n')}`
      : '',
    workout.scoring_type ? `\n\n**Scoring:** ${formatScoringType(workout.scoring_type)}` : '',
    workout.equipment?.length ? `\n\n**Equipment:** ${workout.equipment.join(', ')}` : '',
  ].join('');

  return withoutJson ? `${withoutJson}\n\n${summary}` : summary;
}

function formatScoringType(type: string): string {
  const types: Record<string, string> = {
    time: 'For Time (fastest wins)',
    reps: 'For Reps (most reps wins)',
    rounds: 'AMRAP (most rounds wins)',
    load: 'For Load (heaviest wins)',
    distance: 'For Distance (furthest wins)',
    calories: 'For Calories (most cals wins)',
    points: 'For Points (highest wins)',
  };
  return types[type] || type;
}

// Track type from API
interface Track {
  id: string;
  name: string;
  description: string;
}

export function ChatSidebar({ isOpen, onClose, onWorkoutGenerated }: ChatSidebarProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [extractedWorkout, setExtractedWorkout] = useState<WorkoutData | null>(null);
  const [useLocalFallback, setUseLocalFallback] = useState(false);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [tracksLoading, setTracksLoading] = useState(false);
  const { getToken } = useAuth();

  // Fetch tracks from API when sidebar opens
  useEffect(() => {
    if (isOpen && tracks.length === 0) {
      const fetchTracks = async () => {
        setTracksLoading(true);
        try {
          const token = await getToken();
          const response = await fetch(`${API_URL}/programming/tracks`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              setTracks(data.data);
            }
          }
        } catch (error) {
          console.error('Failed to fetch tracks:', error);
        } finally {
          setTracksLoading(false);
        }
      };
      fetchTracks();
    }
  }, [isOpen, tracks.length, getToken]);

  // Get quick reply options based on last assistant message
  // Pass extractedWorkout to stop showing question options once workout is generated
  const lastAssistantMessage = [...messages].reverse().find((m) => m.role === 'assistant');
  const quickReplies = getQuickReplies(lastAssistantMessage?.content, !!extractedWorkout);

  // Handle quick reply button click
  const handleQuickReply = (value: string) => {
    if (isLoading) return;
    // Auto-submit the quick reply value
    handleSubmitWithValue(value);
  };

  // Submit with specific value (for quick replies)
  const handleSubmitWithValue = async (value: string) => {
    if (!value.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: value.trim(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    try {
      let aiResponse: string;

      if (useLocalFallback) {
        aiResponse = await simulateAiResponse(value);
      } else {
        try {
          aiResponse = await callAiApi(
            updatedMessages.map((m) => ({ role: m.role, content: m.content }))
          );
        } catch (apiError) {
          console.warn('AI API unavailable, using local simulation:', apiError);
          aiResponse = await simulateAiResponse(value);
        }
      }

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: aiResponse,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI Chat error:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-scroll to bottom when messages change
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally scroll on message count change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Extract workout from assistant messages
  useEffect(() => {
    const lastAssistantMessage = [...messages].reverse().find((m) => m.role === 'assistant');
    if (lastAssistantMessage) {
      const workout = parseWorkoutFromMessage(lastAssistantMessage.content);
      setExtractedWorkout(workout);
    }
  }, [messages]);

  const handleUseWorkout = () => {
    if (extractedWorkout && onWorkoutGenerated) {
      onWorkoutGenerated(extractedWorkout);
      setExtractedWorkout(null);
    }
  };

  // Call the real AI API
  const callAiApi = async (
    allMessages: { role: 'user' | 'assistant'; content: string }[]
  ): Promise<string> => {
    const token = await getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_URL}/ai/chat/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ messages: allMessages }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      // If AI not configured, fall back to local simulation
      if (response.status === 503) {
        setUseLocalFallback(true);
        throw new Error('AI_NOT_CONFIGURED');
      }
      throw new Error(error.error || 'Failed to get AI response');
    }

    const data = await response.json();
    if (data.success && data.data) {
      return data.data.content;
    }
    throw new Error('Invalid response format');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    try {
      let aiResponse: string;

      if (useLocalFallback) {
        // Use local simulation if API not available
        aiResponse = await simulateAiResponse(userMessage.content);
      } else {
        try {
          // Try the real AI API
          aiResponse = await callAiApi(
            updatedMessages.map((m) => ({ role: m.role, content: m.content }))
          );
        } catch (apiError) {
          // Fall back to local simulation if API fails
          console.warn('AI API unavailable, using local simulation:', apiError);
          aiResponse = await simulateAiResponse(userMessage.content);
        }
      }

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: aiResponse,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI Chat error:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Simulate AI response - TODO: Replace with API call when AI agent is deployed
  const simulateAiResponse = async (userInput: string): Promise<string> => {
    await new Promise((resolve) => setTimeout(resolve, 800));

    const input = userInput.toLowerCase();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    if (input.includes('sprint') && input.includes('shoot')) {
      return `I'll create a Sprint + Shoot workout for you!

\`\`\`json
{
  "name": "500m Sprint & Shoot",
  "description": "Fast-paced test of speed and accuracy",
  "movements": ["500m Row", "5 Target Shots"],
  "scoring_type": "time",
  "scoring_direction": "ascending",
  "equipment": ["Concept2 Rower", "ErgShoot Target"],
  "scheduled_date": "${tomorrowStr}"
}
\`\`\`

This workout combines a 500m row sprint with target shooting. Click "Use This" to fill the form!`;
    }

    if (input.includes('amrap')) {
      const movements = input.includes('row') ? 'Row' : input.includes('bike') ? 'BikeErg' : 'Ski';
      return `Here's an AMRAP workout for you!

\`\`\`json
{
  "name": "10 Min AMRAP",
  "description": "As many rounds as possible in 10 minutes",
  "movements": ["250m ${movements}", "5 Burpees"],
  "scoring_type": "rounds",
  "scoring_direction": "descending",
  "equipment": ["Concept2 ${movements === 'Row' ? 'Rower' : movements}"],
  "scheduled_date": "${tomorrowStr}"
}
\`\`\`

This is a 10-minute AMRAP. Click "Use This" to fill the form with these details.`;
    }

    if (input.includes('biathlon')) {
      return `Creating a Biathlon-style workout!

\`\`\`json
{
  "name": "ErgShoot Biathlon",
  "description": "Alternating erg and shooting rounds",
  "movements": ["500m Row", "5 Target Shots", "500m Row", "5 Target Shots"],
  "scoring_type": "time",
  "scoring_direction": "ascending",
  "equipment": ["Concept2 Rower", "ErgShoot Target"],
  "scheduled_date": "${tomorrowStr}"
}
\`\`\`

This biathlon workout alternates between rowing and shooting. Ready to use it?`;
    }

    return `I can help you create workouts! Try describing what you want, like:

- "Create a 500m sprint and shoot workout"
- "10 min AMRAP with rowing and burpees"
- "Biathlon style workout for tomorrow"

I'll generate the workout details for you in JSON format that you can review and save.`;
  };

  return (
    <div
      className={cn(
        'fixed right-0 top-0 z-50 h-full w-96 transform bg-background border-l transition-transform duration-300 ease-in-out shadow-xl',
        isOpen ? 'translate-x-0' : 'translate-x-full'
      )}
    >
      <Card className="flex flex-col h-full rounded-none border-0">
        {/* Header */}
        <CardHeader className="flex-shrink-0 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Assistant
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Describe a workout and I'll create it for you.
          </p>
        </CardHeader>

        {/* Messages */}
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
              <Bot className="h-12 w-12 mb-4 opacity-50" />
              <p className="font-medium">What would you like to create?</p>
              <p className="text-sm mt-2 mb-4">Choose a workout type to get started:</p>

              {/* Workout Type Selection */}
              <div className="w-full space-y-3">
                <p className="text-xs font-medium text-muted-foreground/80 uppercase tracking-wide">
                  Workout Types
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start gap-2 hover:bg-primary hover:text-primary-foreground"
                    onClick={() => handleQuickReply('I want to create a Sprint + Shoot workout')}
                  >
                    <span>🎯</span> Sprint + Shoot
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start gap-2 hover:bg-primary hover:text-primary-foreground"
                    onClick={() => handleQuickReply('I want to create an AMRAP workout')}
                  >
                    <span>🔄</span> AMRAP
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start gap-2 hover:bg-primary hover:text-primary-foreground"
                    onClick={() => handleQuickReply('I want to create a For Time workout')}
                  >
                    <span>⏱️</span> For Time
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start gap-2 hover:bg-primary hover:text-primary-foreground"
                    onClick={() => handleQuickReply('I want to create a Biathlon style workout')}
                  >
                    <span>🏅</span> Biathlon
                  </Button>
                </div>

                {/* Dynamic Tracks from Database */}
                {tracks.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-muted-foreground/80 uppercase tracking-wide mt-4">
                      Target Track
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {tracks.slice(0, 6).map((track) => (
                        <Button
                          key={track.id}
                          variant="secondary"
                          size="sm"
                          className="text-xs"
                          onClick={() =>
                            handleQuickReply(
                              `I want to create a workout for the ${track.name} track`
                            )
                          }
                          title={track.description}
                        >
                          {track.name}
                        </Button>
                      ))}
                    </div>
                  </>
                )}
                {tracksLoading && (
                  <p className="text-xs text-muted-foreground">Loading tracks...</p>
                )}
              </div>

              <p className="text-xs mt-4 text-muted-foreground/70">
                Or type your own description below
              </p>
            </div>
          ) : (
            messages.map((message) => {
              // For assistant messages, format to hide JSON
              const workout =
                message.role === 'assistant' ? parseWorkoutFromMessage(message.content) : null;
              const displayContent =
                message.role === 'assistant'
                  ? formatMessageForDisplay(message.content, workout)
                  : message.content;

              return (
                <div
                  key={message.id}
                  className={cn(
                    'flex gap-3',
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 rounded-full bg-primary/10 p-2">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'rounded-lg p-3 max-w-[80%]',
                      message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    )}
                  >
                    <div className="text-sm whitespace-pre-wrap">
                      {displayContent.split(/\*\*(.+?)\*\*/g).map((part, i) =>
                        i % 2 === 1 ? (
                          // biome-ignore lint/suspicious/noArrayIndexKey: Static text split
                          <strong key={`bold-${i}`}>{part}</strong>
                        ) : (
                          // biome-ignore lint/suspicious/noArrayIndexKey: Static text split
                          <span key={`text-${i}`}>{part}</span>
                        )
                      )}
                    </div>
                  </div>
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 rounded-full bg-muted p-2">
                      <MessageSquare className="h-4 w-4" />
                    </div>
                  )}
                </div>
              );
            })
          )}

          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 rounded-full bg-primary/10 p-2">
                <Bot className="h-4 w-4 text-primary animate-pulse" />
              </div>
              <div className="rounded-lg p-3 bg-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            </div>
          )}

          {/* Quick Reply Buttons - inline with messages */}
          {quickReplies.length > 0 && !isLoading && (
            <div className="flex flex-wrap gap-2 mt-2 ml-10">
              {quickReplies.map((option) => (
                <Button
                  key={option.value}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickReply(option.value)}
                  className="text-xs hover:bg-primary hover:text-primary-foreground transition-colors border-primary/50"
                >
                  {option.label}
                </Button>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </CardContent>

        {/* Extracted Workout Action */}
        {extractedWorkout && onWorkoutGenerated && (
          <div className="flex-shrink-0 border-t p-4 bg-green-500/10">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <p className="font-medium text-green-600">Workout Ready</p>
                <p className="text-muted-foreground">{extractedWorkout.name}</p>
              </div>
              <Button size="sm" onClick={handleUseWorkout} className="gap-2">
                <Sparkles className="h-4 w-4" />
                Use This
              </Button>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="flex-shrink-0 border-t p-4">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                quickReplies.length > 0 ? 'Or type your own response...' : 'Describe a workout...'
              }
              disabled={isLoading}
              className="flex-1"
            />
            <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
